import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ROUTES, SUPPORT_EMAIL } from "@/shared/config/constants";
import { useReportError } from "@/contexts/ReportErrorContext";
import { BugAntIcon } from "@heroicons/react/24/outline";

const WHATSAPP_URL = "https://wa.me/18293026892";

export const FooterSection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const openReportModal = useReportError();
  const isSpanish = i18n.language === "es";

  const legalLinks = [
    { label: t("landing.footer.links.legal.terms"), href: ROUTES.TERMS },
    { label: t("landing.footer.links.legal.privacy"), href: ROUTES.PRIVACY },
    { label: t("landing.footer.links.legal.cookies"), href: ROUTES.COOKIES },
  ];

  return (
    <footer className="bg-tiber-950 text-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        {/* Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
          {/* Brand */}
          <div className="flex flex-col items-center md:items-start">
            <img
              src="/images/brand/lawgentia-logo-white.png"
              className="w-20 h-20"
              alt="Lawgentia Logo"
              draggable={false}
            />
            <p className="text-white/60 font-light text-sm mt-2 max-w-xs text-center md:text-left">
              {isSpanish ? "Inteligencia artificial legal" : "Legal AI"}
            </p>
            <p className="text-white/50 text-xs mt-3">
              {t("landing.footer.madeWith")}
            </p>
          </div>

          {/* Legal */}
          <div className="flex flex-col items-center md:items-start">
            <h4 className="font-bold text-white mb-3">
              {t("landing.footer.sections.legal")}
            </h4>
            <nav className="flex flex-col gap-2">
              {legalLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-white/70 hover:text-white font-light text-sm transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div className="flex flex-col items-center md:items-start">
            <h4 className="font-bold text-white mb-3">
              {isSpanish ? "Contacto" : "Contact"}
            </h4>
            <div className="flex flex-col gap-2 text-sm text-white/70">
              <button
                type="button"
                onClick={() => openReportModal()}
                className="hover:text-white transition-colors text-left flex items-center gap-2"
              >
                <BugAntIcon className="w-4 h-4 shrink-0" />
                {t("errorBoundary.reportError")}
              </button>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="hover:text-white transition-colors cursor-pointer"
              >
                Get Support
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors flex items-center gap-2 cursor-pointer"
              >
                {isSpanish ? "829-302-6892" : "+1 829 302 6892"}
              </a>
              <a
                href="https://nolimstudios.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/70 hover:text-white transition-colors text-xs mt-1"
              >
                nolimstudios.com
              </a>
            </div>
          </div>
        </div>

        {/* Legal Disclaimer */}
        <div className="border-t border-white/10 pt-6 mb-4">
          <p className="text-white/50 text-xs text-center font-light leading-relaxed max-w-3xl mx-auto">
            {t("landing.footer.disclaimer")}
          </p>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center text-xs md:text-sm text-white/60 gap-2">
          <p className="font-light">{t("landing.footer.copyright")}</p>
          <div className="flex items-center gap-6">
            {/* <a
              href={SOCIAL_LINKS.github}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              GitHub
            </a>
            <a
              href={SOCIAL_LINKS.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              LinkedIn
            </a> */}
            <p className="font-light">
              {isSpanish ? "Operado por" : "Operated by"}{" "}
              <a
                href="https://nolimstudios.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-tiber-200 hover:text-tiber-500 transition-colors cursor-pointer"
              >
                Nolim Studios LLC
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
